import { useAuth, useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useCallback, useRef, useState } from 'react'

import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import {
  Loader,
  ToolTipWrapper,
  TOP_TIP_POSITION,
  Button,
  BUTTON_SIZE,
  BUTTON_TYPES,
  LoaderSVG,
} from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { addGlobalMessage } from '@src/quince-site-common/src/organisms/appLayout'
import { TOAST_TYPES } from '@src/quince-site-common/src/organisms/appLayout/molecules/globalCommunicationWrapper'
import orderAggregatorService from '@src/quince-site-common/src/services/orderAggregatorService/orderAggregatorService.service'
import { OrderAggregatorErrorCodes } from '@src/quince-site-common/src/services/orderAggregatorService/orderAggregatorService.types'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import BackArrowIcon from '@src/quince-site-integrated/src/assets/Arrow.svg'
import InfoOutlineIcon from '@src/quince-site-integrated/src/assets/info_outline.svg'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { InfiniteListProvider, useInfiniteList } from '../context/infinite-list.context'
import { getFmoTrackingId, TOAST_DURATION } from '../utils'

import styles from './orderList.module.scss'

interface Props {
  setStep: (step: number) => void
  setShowConfirmationModal: (show: boolean) => void
  onSignOut: () => void
}

interface ProductImage {
  thumbnailUrl: string
  productId: string
}

const ImageGallery = ({ thumbnailUrls }: { thumbnailUrls: ProductImage[] }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null)

  const getAltText = (index: number) => `Product ${index + 1}`

  const handleClick = async (data: ProductImage, index: number) => {
    try {
      setLoadingIndex(index)
      const products = await orderAggregatorService.getWebsiteProducts([data.productId])
      const slug = products?.[0]?.slug
      if (slug) {
        window.open(`${window?.location?.origin}/${slug}`, '_blank')
      } else {
        captureException(new Error('Product slug not found'), {
          feature: FEATURES.FIND_MY_ORDER,
          module: MODULES.FIND_MY_ORDER,
          extraInfo: {
            // @i18n-ignore
            capturedFrom: `Product slug not found in OrderList.tsx for productId ${data.productId}`,
          },
        })
      }
    } catch (error) {
      captureException(new Error('Error fetching products'), {
        feature: FEATURES.FIND_MY_ORDER,
        module: MODULES.PRODUCT_CARD,
        extraInfo: {
          // @i18n-ignore
          error,
          capturedFrom: `Error in fetch getWebsiteProducts in OrderList.tsx for productId ${data.productId}`,
        },
      })
      console.error('Error fetching products', error)
    } finally {
      setLoadingIndex(null)
    }
  }

  return (
    <div className={styles.imageGalleryWrapper}>
      <div className={styles.itemImagesContainer} ref={scrollContainerRef}>
        {thumbnailUrls.map((data, index) => (
          <div
            key={`${data?.thumbnailUrl}-${index}`}
            className={styles.imageWrapper}
            onClick={() => {
              void handleClick(data, index)
            }}
          >
            <img className={styles.itemImage} src={data?.thumbnailUrl} alt={getAltText(index)} />
            {loadingIndex === index && (
              <div className={styles.loaderOverlay}>
                <Loader loadWrapperClassName={styles.loader} showLoader={true} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const OrderListComponent = (props: Props) => {
  const { setStep, setShowConfirmationModal, onSignOut } = props
  const { orders, isLoading, loaderRef, hasMore, error: _error } = useInfiniteList()
  const { t } = useTranslation()
  const fmoOrderSearchPeriodValue = useStatsigDynamicConfig<number>('fmoOrderSearchPeriodValue')
  const fmoOrderSearchPeriodUnit = useStatsigDynamicConfig<string>('fmoOrderSearchPeriodUnit')

  const [loadingOrderIds, _] = useState<Set<number>>(new Set())

  const { isLoggedIn } = useAuth()
  const { user } = useCurrentUser()

  const { customerMetadata } = user ?? {}

  if (orders.length === 0 && !isLoading) {
    return <div>{t('no_orders_found')}</div>
  }

  const handleRedirection = (order: any) => {
    const orderId = order?.orderId
    if (!isLoggedIn) {
      onSignOut()
    } else if (isLoggedIn && order?.isPlacedByUser && orderId) {
      window.open(`${window.location.origin}/account/orders/${orderId}`, '_blank')
    } else {
      trackEvent(TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED, {
        properties: {
          actionName: TELEMETRY_EVENT_NAMES.FMO_SWITCH_ACCOUNT_CTA_CLICKED,
          actionData: `FMO Switch Account CTA Clicked`,
          ...getFmoTrackingId(customerMetadata),
        },
      })
      setShowConfirmationModal(true)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return ''
    // eslint-disable-next-line i18next/no-literal-string
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(date)
  }

  return (
    <div className={styles.orderListContainer}>
      <div className={styles.backArrowContainer}>
        <BackArrowIcon
          type="button"
          className={styles.backArrowIcon}
          onClick={() => {
            setStep(0)
          }}
          aria-label={t('go_back')}
        />
      </div>
      <div className={styles.orderListTitle}>
        {t(`order_placed_in_last_${fmoOrderSearchPeriodUnit}`, { count: fmoOrderSearchPeriodValue })}
      </div>
      {orders.map((order, orderIndex) => (
        <div className={styles.orderItemContainer} key={order.orderNumber || order.orderId}>
          <div className={styles.orderContainerRow}>
            <div>
              <div className={classNames(styles.orderItemSubtitle, styles.orderItemEmailInfo)}>
                <div>
                  {t('account')}: <span> {order.maskedEmail}</span>
                </div>
                {order?.isApplePrivateRelay && (
                  <ToolTipWrapper
                    className={styles.toolTipWrapper}
                    toolTipContentClassName={classNames(styles.tooltip, orderIndex === 0 && styles.tooltipFirstOrder)}
                    topTipPosition={TOP_TIP_POSITION.TOP}
                    toolTipContentRenderer={() => t('hide_apple_email_text')}
                  >
                    <InfoOutlineIcon className={styles.infoIcon} />
                  </ToolTipWrapper>
                )}
              </div>
              {order?.orderNumber && (
                <div className={styles.orderItemSubtitle}>
                  {t('order_number')}: <span>{order.orderNumber}</span>
                </div>
              )}
              {order?.createdAt && (
                <div className={styles.orderItemSubtitle}>
                  {t('order_date')}: <span>{formatDate(order?.createdAt)}</span>
                </div>
              )}
            </div>

            <Button
              className={styles.signInButton}
              type={BUTTON_TYPES.SECONDARY}
              size={BUTTON_SIZE.LARGE}
              disabled={loadingOrderIds.has(orderIndex)}
              onClick={() => {
                handleRedirection(order)
              }}
              showLoader={loadingOrderIds.has(orderIndex)}
            >
              {order?.isPlacedByUser && order?.orderId ? t('view_details') : t('sign_in_to_view_details')}
            </Button>
          </div>

          {order?.products && order?.products?.length > 0 && <ImageGallery thumbnailUrls={order.products} />}

          <Button
            className={classNames(styles.signInButton, styles.mobileSignInButton)}
            type={BUTTON_TYPES.SECONDARY}
            size={BUTTON_SIZE.LARGE}
            disabled={loadingOrderIds.has(orderIndex)}
            onClick={() => {
              handleRedirection(order)
            }}
            showLoader={loadingOrderIds.has(orderIndex)}
          >
            {order?.isPlacedByUser && order?.orderId ? t('view_details') : t('sign_in_to_view_details')}
          </Button>
        </div>
      ))}
      {hasMore && (
        <div className={styles.loaderContainer} ref={loaderRef}>
          {isLoading && (
            <div>
              <LoaderSVG />{' '}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
interface OrderListProps {
  setStep: (step: number) => void
  searchParams: any
  initialData?: any
  setShowConfirmationModal: (show: boolean) => void
  onSignOut: () => void
}
export default function OrderList({
  setStep,
  searchParams,
  initialData,
  setShowConfirmationModal,
  onSignOut,
}: OrderListProps) {
  const { t } = useTranslation()

  const handleInfiniteListError = useCallback(
    (error: any) => {
      const errCode = error?.response?.data?.errorCode
      const errMessage = error?.response?.data?.message
      if (errCode === OrderAggregatorErrorCodes.FIND_ORDER_NOT_AUTHORIZED) {
        addGlobalMessage({
          type: TOAST_TYPES.error,
          title: errMessage || t('not_authorized_to_view_orders'),
          position: 'bottom-left',
          duration: TOAST_DURATION,
        })
        captureException(new Error(errMessage || t('not_authorized_to_view_orders')), {
          feature: FEATURES.FIND_MY_ORDER,
          module: MODULES.FIND_MY_ORDER,
          extraInfo: {
            // @i18n-ignore
            capturedFrom: 'User verification time has expired',
          },
        })
        setStep(0)
      } else {
        addGlobalMessage({
          type: TOAST_TYPES.error,
          title: errMessage || t('failed_to_fetch_orders'),
          position: 'bottom-left',
          duration: TOAST_DURATION,
        })
      }
    },
    [t, setStep],
  )

  return (
    <InfiniteListProvider searchParams={searchParams} initialData={initialData} onError={handleInfiniteListError}>
      <OrderListComponent onSignOut={onSignOut} setShowConfirmationModal={setShowConfirmationModal} setStep={setStep} />
    </InfiniteListProvider>
  )
}
